import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { Loader } from "@/components/common/Loader";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api from "@/lib/api";
import { Eye, Inbox, CalendarCheck, Package, Smartphone, Phone, Ticket, LogIn, FileText, TrendingUp } from "lucide-react";

const RANGES = [{ v: "7", l: "Last 7 days" }, { v: "30", l: "Last 30 days" }, { v: "90", l: "Last 90 days" }];

const STAT_DEFS = [
  { key: "pageviews", label: "Page Views", icon: Eye },
  { key: "lead_submissions", label: "Lead Submissions", icon: Inbox },
  { key: "booking_clicks", label: "Booking Clicks", icon: CalendarCheck },
  { key: "package_clicks", label: "Package Clicks", icon: Package },
  { key: "app_download_clicks", label: "App Downloads", icon: Smartphone },
  { key: "contact_clicks", label: "Contact Clicks", icon: Phone },
  { key: "event_registration_clicks", label: "Event Registrations", icon: Ticket },
  { key: "login_clicks", label: "Login / Portal", icon: LogIn },
  { key: "support_ticket_submissions", label: "Support Tickets", icon: FileText },
];

const SOURCE_LABEL = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : "Direct");

export default function AdminAnalytics() {
  const [days, setDays] = useState("30");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/admin/analytics?days=${days}`).then((r) => setData(r.data)).finally(() => setLoading(false));
  }, [days]);

  const maxPv = data ? Math.max(1, ...data.daily_pageviews.map((d) => d.pageviews)) : 1;
  const maxSrc = data && data.top_lead_sources.length ? Math.max(1, ...data.top_lead_sources.map((s) => s.count)) : 1;

  return (
    <AdminLayout>
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="display-title text-4xl">Analytics</h1>
          <p className="mt-1 text-sm text-muted-foreground">First-party engagement &amp; conversion tracking.</p>
        </div>
        <Select value={days} onValueChange={setDays}>
          <SelectTrigger className="w-44" data-testid="analytics-range"><SelectValue /></SelectTrigger>
          <SelectContent>{RANGES.map((r) => <SelectItem key={r.v} value={r.v}>{r.l}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      {loading || !data ? <Loader /> : (
        <div className="mt-6 space-y-8" data-testid="analytics-dashboard">
          {/* Stat cards */}
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3">
            {STAT_DEFS.map((s) => {
              const Icon = s.icon;
              return (
                <div key={s.key} className="rounded-xl border border-border bg-card p-5" data-testid={`stat-${s.key}`}>
                  <div className="flex items-center justify-between">
                    <Icon className="h-5 w-5 text-brand" />
                    <span className="display-title text-3xl">{data.totals[s.key] ?? 0}</span>
                  </div>
                  <p className="mt-2 text-sm text-muted-foreground">{s.label}</p>
                </div>
              );
            })}
          </div>

          {/* Pageviews chart */}
          <section className="rounded-xl border border-border bg-card p-6">
            <h2 className="flex items-center gap-2 display-title text-2xl"><TrendingUp className="h-5 w-5 text-brand" /> Daily Page Views</h2>
            <div className="mt-6 flex h-40 items-end gap-1" data-testid="analytics-pageview-chart">
              {data.daily_pageviews.map((d) => (
                <div key={d.date} className="group relative flex-1" title={`${d.date}: ${d.pageviews}`}>
                  <div className="w-full rounded-t bg-brand/80 transition-colors hover:bg-brand" style={{ height: `${Math.max(2, (d.pageviews / maxPv) * 100)}%` }} />
                </div>
              ))}
            </div>
            <p className="mt-2 text-xs text-muted-foreground">{data.daily_pageviews[0]?.date} → {data.daily_pageviews[data.daily_pageviews.length - 1]?.date}</p>
          </section>

          <div className="grid gap-6 lg:grid-cols-2">
            {/* Top lead sources */}
            <section className="rounded-xl border border-border bg-card p-6">
              <h2 className="display-title text-2xl">Top Lead Sources</h2>
              {data.top_lead_sources.length === 0 ? (
                <p className="mt-4 text-sm text-muted-foreground">No leads in this period.</p>
              ) : (
                <ul className="mt-4 space-y-3" data-testid="analytics-top-sources">
                  {data.top_lead_sources.map((s) => (
                    <li key={s.source}>
                      <div className="flex items-center justify-between text-sm"><span className="font-medium">{SOURCE_LABEL(s.source)}</span><span className="text-muted-foreground">{s.count}</span></div>
                      <div className="mt-1 h-2 w-full rounded-full bg-muted"><div className="h-2 rounded-full bg-brand" style={{ width: `${(s.count / maxSrc) * 100}%` }} /></div>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            {/* Recent leads */}
            <section className="rounded-xl border border-border bg-card p-6">
              <div className="flex items-center justify-between"><h2 className="display-title text-2xl">Recent Leads</h2><Link to="/admin/leads" className="text-xs font-semibold uppercase tracking-wide text-brand hover:underline">View all</Link></div>
              {data.recent_leads.length === 0 ? (
                <p className="mt-4 text-sm text-muted-foreground">No leads yet.</p>
              ) : (
                <ul className="mt-4 space-y-3" data-testid="analytics-recent-leads">
                  {data.recent_leads.map((l) => (
                    <li key={l.id} className="flex items-center justify-between gap-3 border-b border-border pb-2 last:border-0">
                      <div className="min-w-0"><p className="truncate text-sm font-medium">{l.name}</p><p className="truncate text-xs text-muted-foreground">{l.email} · {SOURCE_LABEL(l.source)}</p></div>
                      <span className="shrink-0 text-xs text-muted-foreground">{l.created_at ? new Date(l.created_at).toLocaleDateString() : ""}</span>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </div>

          {/* Recent tickets */}
          <section className="rounded-xl border border-border bg-card p-6">
            <div className="flex items-center justify-between"><h2 className="display-title text-2xl">Recent Support Tickets</h2><Link to="/admin/tickets" className="text-xs font-semibold uppercase tracking-wide text-brand hover:underline">View all</Link></div>
            {data.recent_tickets.length === 0 ? (
              <p className="mt-4 text-sm text-muted-foreground">No tickets yet.</p>
            ) : (
              <ul className="mt-4 space-y-3" data-testid="analytics-recent-tickets">
                {data.recent_tickets.map((t) => (
                  <li key={t.id} className="flex items-center justify-between gap-3 border-b border-border pb-2 last:border-0">
                    <div className="min-w-0"><p className="truncate text-sm font-medium">{t.subject || t.category}</p><p className="truncate text-xs text-muted-foreground">{t.name} · {t.status}</p></div>
                    <span className="shrink-0 text-xs text-muted-foreground">{t.created_at ? new Date(t.created_at).toLocaleDateString() : ""}</span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      )}
    </AdminLayout>
  );
}
